import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button, Field, Input } from '@/components/ui/primitives';
import { useDebounced } from './LabBenchDebounce';

export interface LabInsuredOrderValue {
  diagnosisCode: string;
  diagnosisTitle: string;
  referralNo: string;
  referringLicense: string;
}

export const EMPTY_INSURED_ORDER: LabInsuredOrderValue = { diagnosisCode: '', diagnosisTitle: '', referralNo: '', referringLicense: '' };

const LICENCE = /^[A-Za-z0-9./-]{2,40}$/;

export function insuredOrderReady(value: LabInsuredOrderValue): boolean {
  const licence = value.referringLicense.trim();
  return Boolean(value.diagnosisCode) && (licence === '' || LICENCE.test(licence));
}

export function insuredOrderBody(value: LabInsuredOrderValue) {
  return {
    diagnosisCode: value.diagnosisCode || undefined,
    diagnosisTitle: value.diagnosisTitle || undefined,
    referralNo: value.referralNo.trim() || undefined,
    referringLicense: value.referringLicense.trim() || undefined,
  };
}

interface IcdResult {
  code: string;
  title: string;
}

export function LabInsuredOrderFields({ value, onChange }: { value: LabInsuredOrderValue; onChange: (next: LabInsuredOrderValue) => void }) {
  const { hasModule } = useAuth();
  const nphies = hasModule('nphies');
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term.trim(), 250);
  const results = useQuery({
    queryKey: ['icd', debounced],
    queryFn: () => get<IcdResult[]>(`/icd/search${qs({ q: debounced, limit: 8 })}`),
    enabled: debounced.length >= 2 && !value.diagnosisCode,
    staleTime: 10 * 60_000,
  });
  const licence = value.referringLicense.trim();
  const licenceBad = licence !== '' && !LICENCE.test(licence);

  return (
    <div className="space-y-3 rounded-md border p-3">
      <p className="text-sm font-medium">{tr('lab', 'insurance_details')}</p>
      <Field label={tr('lab', 'diagnosis')} required hint={tr('lab', 'the_insurer_pays_only_for_tests_ordered_for_a')}>
        {value.diagnosisCode ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-mono text-xs text-primary">{value.diagnosisCode}</span>
            <span className="flex-1">{value.diagnosisTitle}</span>
            <Button variant="ghost" size="sm" onClick={() => onChange({ ...value, diagnosisCode: '', diagnosisTitle: '' })} aria-label={tr('lab', 'change_the_diagnosis')}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-1">
            <div className="relative">
              <Search className="pointer-events-none absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input id="lab-order-diagnosis" className="ps-8" placeholder={tr('lab', 'icd_10_code_or_description')} value={term} onChange={(e) => setTerm(e.target.value)} />
            </div>
            {debounced.length >= 2 && (
              <ul className="max-h-48 divide-y overflow-y-auto rounded-md border">
                {(results.data ?? []).map((result) => (
                  <li key={result.code}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-start text-sm hover:bg-accent focus-ring"
                      onClick={() => {
                        onChange({ ...value, diagnosisCode: result.code, diagnosisTitle: result.title });
                        setTerm('');
                      }}
                    >
                      <span className="font-mono text-xs text-primary">{result.code}</span> {result.title}
                    </button>
                  </li>
                ))}
                {results.data?.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">{tr('lab', 'no_match')}</li>}
              </ul>
            )}
          </div>
        )}
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={tr('lab', 'referral_number')} htmlFor="lab-order-referral" hint={tr('lab', 'optional_as_written_on_the_referral_letter')}>
          <Input id="lab-order-referral" maxLength={60} value={value.referralNo} onChange={(e) => onChange({ ...value, referralNo: e.target.value })} />
        </Field>
        <Field
          label={nphies ? tr('lab', 'referring_practitioner_licence_sent_to_nphies') : tr('lab', 'referring_doctors_licence')}
          htmlFor="lab-order-referring-licence"
          hint={nphies ? tr('lab', 'the_scfhs_number_of_the_doctor_who_asked_for') : tr('lab', 'optional_the_licence_or_registration_number_of')}
          error={licenceBad ? 'Enter the licence number without spaces' : undefined}
        >
          <Input id="lab-order-referring-licence" maxLength={40} value={value.referringLicense} onChange={(e) => onChange({ ...value, referringLicense: e.target.value })} />
        </Field>
      </div>
    </div>
  );
}
